import React, { useRef, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { brand, notice } from "../../theme/businessFlowsTheme";

export const dialogPaper = { className: "bfx", sx: { borderRadius: 4 } };
export const menuPaper = { PaperProps: { className: "bfx", sx: { borderRadius: 3, border: `1px solid ${brand.line}` } } };

// ── A notice ───────────────────────────────────────────────────────────────
// What a screen says when something didn't work, or won't work yet: a mark,
// one sentence naming what happened, and one saying what to do about it.
// Tinted paper and a hairline rather than a slab of colour, because most of
// these are a mistyped key or a plan that needs a word with us - not damage.
// `tone` comes from the theme's notice palette.
export function Notice({ tone = notice.quiet, icon, title, children, next, action, role, sx }) {
  return (
    <Box
      role={role}
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        bgcolor: tone.bg,
        border: `1px solid ${tone.line}`,
        borderRadius: "14px",
        px: { xs: 1.75, sm: 2.25 },
        py: { xs: 1.75, sm: 2 },
        ...sx,
      }}
    >
      {icon && (
        <Box aria-hidden sx={{ color: tone.fg, display: "flex", mt: "1px", flexShrink: 0 }}>
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {title && (
          <Typography
            component="p"
            sx={{ fontSize: "0.95rem", fontWeight: 650, color: tone.fg, letterSpacing: "-0.01em", mb: children || next ? 0.4 : 0 }}
          >
            {title}
          </Typography>
        )}
        {children && (
          // A div, not a paragraph: a notice sometimes carries a chip or a
          // list beside its sentence, and those may not sit inside a <p>.
          <Typography component="div" sx={{ fontSize: "0.925rem", lineHeight: 1.65, color: brand.prose, maxWidth: 580 }}>
            {children}
          </Typography>
        )}
        {next && (
          <Typography component="p" sx={{ fontSize: "0.875rem", lineHeight: 1.6, color: brand.muted, maxWidth: 580, mt: 0.6 }}>
            {next}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

// The quiet dismiss on a notice a person has read.
const dismiss = (onClose) =>
  onClose ? (
    <IconButton aria-label="Dismiss" size="small" onClick={onClose} sx={{ color: brand.muted, mt: "-2px", flexShrink: 0 }}>
      <CloseRoundedIcon fontSize="small" />
    </IconButton>
  ) : null;

// Something didn't work. The sentence is the server's own, so the screen never
// invents a reason of its own for a refusal it was given one for.
export function Refusal({ title = "That didn't work", children, next, action, onClose, role, sx }) {
  return (
    <Notice tone={notice.fix} icon={<ErrorOutlineRoundedIcon fontSize="small" />} title={title} next={next} action={action || dismiss(onClose)} role={role} sx={sx}>
      {children}
    </Notice>
  );
}

// Worth reading before carrying on - nothing is broken yet.
export function Caution({ title, children, next, action, onClose, role, sx }) {
  return (
    <Notice tone={notice.wait} icon={<WarningAmberRoundedIcon fontSize="small" />} title={title} next={next} action={action || dismiss(onClose)} role={role} sx={sx}>
      {children}
    </Notice>
  );
}

// How something is, said once, without alarm.
export function Hint({ title, children, next, action, onClose, role, sx }) {
  return (
    <Notice tone={notice.quiet} icon={<InfoOutlinedIcon fontSize="small" />} title={title} next={next} action={action || dismiss(onClose)} role={role} sx={sx}>
      {children}
    </Notice>
  );
}

// One row of a listing, with the rhythm the API keys tab set: air around the
// text, and a hairline between rows rather than the card's own edge.
export const rowSx = (first) => ({
  px: { xs: 2, sm: 2.5 },
  py: 2,
  borderTop: first ? "none" : `1px solid ${brand.hairline}`,
});

// A destructive action stays quiet until the pointer or the keyboard is on it:
// the colour that warns belongs to the dialog that asks, not to the list.
export const quietDanger = {
  color: brand.muted,
  borderColor: brand.line,
  "&:hover": { color: notice.fix.fg, borderColor: notice.fix.line, bgcolor: notice.fix.bg },
  "&.Mui-focusVisible, &:focus-visible": { color: notice.fix.fg, borderColor: notice.fix.line, bgcolor: notice.fix.bg },
};

// Anything that archives asks first. onConfirm returns the API answer; a
// refusal stays in the dialog so the person can read what to do next.
export function ConfirmDialog({ open, title, body, confirmLabel = "Archive", tone = "error", onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Keep the words while the dialog fades out, after the caller has cleared them.
  const shown = useRef({ title, body, confirmLabel });
  if (open) shown.current = { title, body, confirmLabel };
  const close = () => {
    setError("");
    onClose();
  };
  const confirm = async () => {
    setBusy(true);
    setError("");
    const res = await onConfirm();
    setBusy(false);
    if (res?.success === false) setError(res.msg || "That didn't work. Try again.");
    else close();
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="xs" PaperProps={dialogPaper}>
      <DialogTitle sx={{ fontWeight: 700 }}>{shown.current.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {shown.current.body}
        </Typography>
        {error && <Refusal sx={{ mt: 2 }}>{error}</Refusal>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={busy}>
          Cancel
        </Button>
        <LoadingButton variant="contained" color={tone} loading={busy} onClick={confirm}>
          {shown.current.confirmLabel || "Archive"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export function EmptyState({ icon, title, children, action }) {
  return (
    <Stack alignItems="center" spacing={1.25} sx={{ py: 6, px: 2, textAlign: "center" }}>
      {icon && (
        <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.canvas, color: brand.ink }}>
          {icon}
        </Box>
      )}
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      {children && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
          {children}
        </Typography>
      )}
      {action}
    </Stack>
  );
}

export function ListSkeleton({ rows = 4, height = 64 }) {
  return (
    <Stack spacing={1}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={height} sx={{ borderRadius: 3 }} />
      ))}
    </Stack>
  );
}


// A rounded surface for one list or form.
export function Surface({ children, sx }) {
  return <Box sx={{ border: `1px solid ${brand.line}`, borderRadius: 4, bgcolor: "#fff", overflow: "hidden", ...sx }}>{children}</Box>;
}

// Focus rings that show when moving with the keyboard, for every control inside.
export const focusRing = {
  "& .Mui-focusVisible, & .MuiButtonBase-root:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
};

// A toast naming what happened. notice: "" | "text" | { text, tone }.
export function Toast({ notice, onClose }) {
  const text = typeof notice === "string" ? notice : notice?.text;
  const tone = typeof notice === "string" ? "success" : notice?.tone || "success";
  const last = useRef({ text, tone });
  if (text) last.current = { text, tone };
  return (
    <Snackbar open={Boolean(text)} autoHideDuration={5000} onClose={onClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
      <Alert onClose={onClose} severity={last.current.tone} variant="filled" sx={{ borderRadius: 3, alignItems: "center", minWidth: { sm: 320 } }}>
        {last.current.text}
      </Alert>
    </Snackbar>
  );
}
